import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { ADMIN_PLUS, FULL_MEMBER_ROLES } from "../constants";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { requireWorkspaceRole } from "./helpers";
import { makeWorkspaceId, makeUserId } from "../types/branded-ids";

export const channelsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      workspace_id: z.string(),
      // Bound the name at the write boundary — public-workspace SEO surfaces
      // (/c/:name + sitemap + crawler HTML) interpolate channel names into URLs
      // and payloads, so an unbounded name would mean unbounded output.
      name: z.string().min(1).max(255),
      description: z.string().optional().nullable(),
      topic: z.string().optional().nullable(),
      is_private: z.boolean().default(false),
      is_archived: z.boolean().optional(),
      posting_restriction: z.string().optional(),
      retention_days_override: z.number().optional().nullable(),
      type: z.string().optional().nullable(),
      // WS-B1: channel region. For workspace channels this should match
      // workspace.region (caller responsibility for now; WS-B13a's
      // workspace-create UI passes through correctly). DMs (workspace_id
      // null path in actions-dm) compute via lower-userId rule (WS-B4).
      region: z.string().optional(),
      created_by: z.string(),
      created_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.created_by !== ctx.sub) {
        throw new Error("Unauthorized: created_by mismatch");
      }
      // Spaces must flow through clientSpacesMutators.insert (which writes
      // both the channels row AND the creator's channel_members manager
      // row in one transaction). Without that, callers could produce an
      // orphan space via this generic path. Same closure rationale as
      // parent_id (excluded from the Zod schema entirely).
      if (args.type === "space") {
        throw new Error("Unauthorized: use clientSpacesMutators.insert");
      }
      if (tx.location === "server") {
        const existing = await tx.run(zql.channels.where("id", args.id).one());
        if (existing) {
          // Cross-workspace takeover: reject id reuse across workspaces
          // with an explicit error so the caller knows their id is invalid.
          if (existing.workspace_id !== args.workspace_id) {
            throw new Error(
              "Unauthorized: channel id belongs to a different workspace",
            );
          }
          // Insert is for creation only. Existing channel in the same
          // workspace → return early without re-upserting. Modifications
          // must go through channels.update which has admin+/channel-
          // manager auth. This blocks privilege escalation via upsert,
          // including the creator-but-not-admin case where a regular
          // member could otherwise overwrite is_private,
          // posting_restriction, or retention_days_override (fields that
          // channels.update intentionally does not allow non-admins to
          // change). is_private is in fact absent from channels.update's
          // schema entirely — meant to be immutable post-creation.
          return;
        }
        // New channel — must be a full workspace member to create
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(args.workspace_id),
          makeUserId(ctx.sub),
          [...FULL_MEMBER_ROLES],
        );
      }
      await tx.mutate.channels.upsert({
        ...args,
        is_archived: args.is_archived ?? false,
        posting_restriction: args.posting_restriction ?? "everyone",
        region: args.region ?? "us",
      });
    },
  ),
  update: defineMutator(
    z.object({
      id: z.string(),
      name: z.string().min(1).max(255).optional(),
      description: z.string().optional().nullable(),
      topic: z.string().optional().nullable(),
      is_archived: z.boolean().optional(),
      posting_restriction: z.string().optional(),
      retention_days_override: z.number().optional().nullable(),
    }),
    async ({ tx, args, ctx }) => {
      // Type check first, on BOTH client and server, so optimistic UI
      // doesn't briefly show a renamed space before server reconciliation
      // reverts. tx.run on the client returns cached data — if the channel
      // is in the local cache (which is the common case for any space the
      // user can see), the type check fires and the optimistic mutation
      // is blocked. If not cached, the early return defers to server.
      // Spaces must flow through clientSpacesMutators.update so the
      // cascade-archive logic + space-manager auth kicks in.
      const channel = await tx.run(zql.channels.where("id", args.id).one());
      if (channel?.type === "space") {
        throw new Error("Unauthorized: use clientSpacesMutators.update");
      }
      if (tx.location === "server") {
        if (!channel) return;
        if (!channel.workspace_id) {
          // DM/group_dm — only creator can update
          if (channel.created_by !== ctx.sub) {
            throw new Error("Unauthorized: not DM creator");
          }
        } else {
          // Workspace channel — admin+ OR channel manager
          const wsRole = await tx.run(
            zql.workspace_members
              .where("workspace_id", channel.workspace_id)
              .where("user_id", ctx.sub)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (wsRole && ADMIN_PLUS.includes(wsRole.role)) {
            // Admin+ — allowed
          } else {
            const chMember = await tx.run(
              zql.channel_members
                .where("channel_id", args.id)
                .where("user_id", ctx.sub)
                .where("removed_at", "IS", null)
                .one(),
            );
            if (!chMember?.is_channel_manager) {
              throw new Error("Unauthorized: not admin or channel manager");
            }
          }
        }
      }
      await tx.mutate.channels.update(args);
    },
  ),
  delete: defineMutator(z.object({ id: z.string() }), async () => {
    // Server-authoritative — deletes go through delete-routes.ts
    throw new Error("Unauthorized: use server delete endpoint");
  }),
};

export const channelMembersMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      channel_id: z.string(),
      user_id: z.string(),
      is_channel_manager: z.boolean().optional(),
      joined_at: z.number(),
      muted: z.boolean().optional(),
      notify: z.string().optional(),
    }),
    async ({ tx, args, ctx }) => {
      // Only self-join via public mutator (cheap args check, both sides)
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: use server API to add channel members");
      }
      if (tx.location === "server") {
        // Block channel_members row hijack via id-collision upsert.
        // If a channel_members row with this id already exists and belongs
        // to someone else, the upsert would replace user_id and effectively
        // kick the victim. Only allow re-upsert by the original owner.
        const existing = await tx.run(
          zql.channel_members.where("id", args.id).one(),
        );
        if (existing && existing.user_id !== ctx.sub) {
          throw new Error(
            "Unauthorized: cannot upsert another user's channel membership",
          );
        }

        const channel = await tx.run(
          zql.channels.where("id", args.channel_id).one(),
        );
        if (!channel) throw new Error("Channel not found");
        // Spaces (channels.type='space', post mig 120) require admin-only
        // membership grants via the server endpoint
        // (POST /api/client-spaces/:id/members) — not the public mutator.
        // Without this rejection, any full workspace member could
        // self-join a public space via Zero, bypassing audit logging,
        // notification dispatch, and the auto-join cascade to child
        // channels. PR #1250 round-3 self-review finding.
        if (channel.type === "space") {
          throw new Error(
            "Unauthorized: space membership goes through the server endpoint",
          );
        }
        if (channel.is_private) {
          throw new Error("Unauthorized: cannot self-join private channel");
        }
        if (channel.is_archived) {
          throw new Error("Unauthorized: cannot join archived channel");
        }
        if (!channel.workspace_id) {
          throw new Error(
            "Unauthorized: DM membership managed by action mutators",
          );
        }
        const wsMember = await tx.run(
          zql.workspace_members
            .where("workspace_id", channel.workspace_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (
          !wsMember ||
          !(FULL_MEMBER_ROLES as readonly string[]).includes(wsMember.role)
        ) {
          throw new Error("Unauthorized: not a full workspace member");
        }
        // Force is_channel_manager to false on self-join
        args = { ...args, is_channel_manager: false };

        // Resurrect a tombstone row if one exists for (channel_id,
        // user_id). Zero's upsert resolves on the PK (`id`) — but the
        // caller passes a fresh UUID and the publication row filter
        // hides the tombstone's id from the client. Without this
        // lookup + update-by-id, the upsert path attempts an INSERT
        // that collides with `channel_members_ch_user_unique`. See
        // the analogous server-side ON CONFLICT DO UPDATE pattern.
        const tombstone = await tx.run(
          zql.channel_members
            .where("channel_id", args.channel_id)
            .where("user_id", ctx.sub)
            .one(),
        );
        if (tombstone && tombstone.removed_at != null) {
          await tx.mutate.channel_members.update({
            id: tombstone.id,
            is_channel_manager: false,
            joined_at: args.joined_at,
            muted: args.muted ?? false,
            notify: args.notify ?? "all",
            hidden: false,
            removed_at: null,
            removed_by: null,
          });
          return;
        }
      }
      // Clear removed_at/removed_by on rejoin — a user who was previously
      // kicked and is now re-adding themselves should be a fully active
      // member again, not a tombstone. Explicit null (not undefined) so
      // the upsert sets the column rather than preserving the stale value.
      await tx.mutate.channel_members.upsert({
        ...args,
        is_channel_manager: args.is_channel_manager ?? false,
        muted: args.muted ?? false,
        notify: args.notify ?? "all",
        hidden: false,
        removed_at: null,
        removed_by: null,
      });
    },
  ),
  update: defineMutator(
    z.object({
      id: z.string(),
      is_channel_manager: z.boolean().optional(),
      muted: z.boolean().optional(),
      notify: z.string().optional(),
      hidden: z.boolean().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const membership = await tx.run(
          zql.channel_members.where("id", args.id).one(),
        );
        if (!membership) return;

        if (membership.user_id === ctx.sub) {
          // Self-update: preferences allowed, but not self-granting manager
          if (args.is_channel_manager === true) {
            const channel = await tx.run(
              zql.channels.where("id", membership.channel_id).one(),
            );
            let isAdmin = false;
            if (channel?.workspace_id) {
              const wsRole = await tx.run(
                zql.workspace_members
                  .where("workspace_id", channel.workspace_id)
                  .where("user_id", ctx.sub)
                  .where("removed_at", "IS", null)
                  .one(),
              );
              isAdmin = !!wsRole && ADMIN_PLUS.includes(wsRole.role);
            }
            if (!isAdmin) {
              throw new Error(
                "Unauthorized: only admins can grant channel manager",
              );
            }
          }
        } else {
          // Updating someone else — admin+ only, is_channel_manager field only
          const allowedCrossUserFields = ["id", "is_channel_manager"];
          const extraFields = Object.keys(args).filter(
            (k) =>
              !allowedCrossUserFields.includes(k) &&
              (args as Record<string, unknown>)[k] !== undefined,
          );
          if (extraFields.length > 0) {
            throw new Error(
              "Unauthorized: can only change is_channel_manager on other members",
            );
          }
          const channel = await tx.run(
            zql.channels.where("id", membership.channel_id).one(),
          );
          if (!channel?.workspace_id) {
            throw new Error("Unauthorized: cannot modify DM membership");
          }
          const wsRole = await tx.run(
            zql.workspace_members
              .where("workspace_id", channel.workspace_id)
              .where("user_id", ctx.sub)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (!wsRole || !ADMIN_PLUS.includes(wsRole.role)) {
            throw new Error(
              "Unauthorized: only workspace admins can assign managers",
            );
          }
        }
      }
      await tx.mutate.channel_members.update(args);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const membership = await tx.run(
          zql.channel_members.where("id", args.id).one(),
        );
        if (!membership) return;

        // Spaces (channels.type='space', post mig 120) — leaving a space
        // requires the server endpoint (DELETE /api/client-spaces/:id/
        // members/:userId) so the cascade-unjoin logic for child channels
        // runs. Without this guard, a user calling
        // mutators.channel_members.delete on their space membership row
        // would un-join the space but stay subscribed to all its child
        // channels — confusing UX and stale notifications. Round-5
        // self-review (matching the round-3 self-join rejection on
        // channelMembersMutators.insert).
        const targetChannel = await tx.run(
          zql.channels.where("id", membership.channel_id).one(),
        );
        if (targetChannel?.type === "space") {
          throw new Error(
            "Unauthorized: leaving a space requires the server endpoint",
          );
        }

        // Self-remove always allowed
        if (membership.user_id !== ctx.sub) {
          // Removing others — need admin or channel manager
          const channel = await tx.run(
            zql.channels.where("id", membership.channel_id).one(),
          );
          let isAdmin = false;
          if (channel?.workspace_id) {
            const wsRole = await tx.run(
              zql.workspace_members
                .where("workspace_id", channel.workspace_id)
                .where("user_id", ctx.sub)
                .where("removed_at", "IS", null)
                .one(),
            );
            isAdmin = !!wsRole && ADMIN_PLUS.includes(wsRole.role);
          }
          if (!isAdmin) {
            const myMembership = await tx.run(
              zql.channel_members
                .where("channel_id", membership.channel_id)
                .where("user_id", ctx.sub)
                .where("removed_at", "IS", null)
                .one(),
            );
            if (!myMembership?.is_channel_manager) {
              throw new Error("Unauthorized: not admin or channel manager");
            }
          }
        }
      }
      // Soft-delete: preserves the row so historical authorship, reactions,
      // and branch-2/3 user visibility continue to resolve. The
      // `on_workspace_member_removal` trigger (Step 1b) handles cascading
      // cleanup of dependent state (typing_in, channel_notification_overrides,
      // etc.) when set from NULL → NOT NULL.
      //
      // ALSO clear `is_channel_manager` so a kicked manager cannot silently
      // regain manager privileges via tombstone resurrection on rejoin —
      // every other soft-delete path (workspace-member kick trigger, the
      // /client-spaces/:id/members DELETE route) already clears this; the
      // public mutator must match. Without it, the `ON CONFLICT DO UPDATE`
      // clauses on rejoin (which intentionally omit `is_channel_manager`
      // from the SET list to preserve role for active members) would
      // resurrect the kicked manager as a manager.
      await tx.mutate.channel_members.update({
        id: args.id,
        is_channel_manager: false,
        removed_at: Date.now(),
        removed_by: ctx?.sub,
      });
    },
  ),
};
